import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faPlus,
  faEdit,
  faTrash,
  faCalculator,
  faRefresh,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import StaffCalculationRuleModal from './StaffCalculationRuleModal';
import { staffCalculationRulesApi, ruleHelpers } from './actions';
import './staff-calculation-rules.css';

const StaffCalculationRules = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const workModes = [
    '4 години',
    '9 годин (5-ти денний робочий день)',
    '9 годин (6-ти денний робочий день)',
    '10,5 годин (5-ти денний робочий день)',
    '10,5 годин (6-ти денний робочий день)',
    '12 годин (5-ти денний робочий день)',
    '12 годин (6-ти денний робочий день)',
    '24 години (5-ти денний робочий день)',
    '24 години (6-ти денний робочий день)'
  ];

  const columns = [
    {
      key: 'position.name',
      label: 'Посада',
      sortable: true,
      width: '20%'
    },
    {
      key: 'ruleType',
      label: 'Тип правила',
      sortable: true,
      width: '15%'
    },
    {
      key: 'conditions',
      label: 'Умови',
      sortable: false,
      width: '20%'
    },
    {
      key: 'rulePerGroupTxt',
      label: 'Пропорційно кількості груп',
      sortable: false,
      width: '10%'
    },
    {
      key: 'maxUnits',
      label: 'Макс. шт. од.',
      sortable: true,
      width: '10%'
    },
    {
      key: 'createdAt',
      label: 'Дата створення',
      sortable: true,
      width: '15%'
    },
    {
      key: 'actions',
      label: 'Дії',
      sortable: false,
      width: '10%'
    }
  ];

  // Загрузка данных
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [rulesResponse, positionsResponse] = await Promise.all([
        staffCalculationRulesApi.getAllRules({ 
          isActive: activeTab === 'active' ? 'true': 'false' 
        }),
        staffCalculationRulesApi.getAvailablePositions()
      ]);

      setRules(rulesResponse.rules || []);
      setPositions(positionsResponse || []);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Помилка завантаження даних';
      setError(errorMessage);
      //toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return rules;
    
    return [...rules].sort((a, b) => {
      let aValue = a;
      let bValue = b;
      
      // Обработка вложенных свойств
      if (sortConfig.key.includes('.')) {
        const keys = sortConfig.key.split('.');
        aValue = keys.reduce((obj, key) => obj[key], a);
        bValue = keys.reduce((obj, key) => obj[key], b);
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [rules, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index, e) => {
    e.stopPropagation();
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleActionSelect = async (actionId, rule) => {
    try {
      switch (actionId) {
        case 'edit':
          setSelectedRule(rule);
          setIsModalOpen(true);
          break;

        case 'archive':
          await staffCalculationRulesApi.archiveRule(rule.id);
          //toast.success('Правило успішно архівовано');
          await loadData();
          break;

        case 'restore':
          await staffCalculationRulesApi.restoreRule(rule.id);
          //toast.success('Правило успішно відновлено');
          await loadData();
          break;

        case 'delete':
          if (window.confirm('Ви впевнені, що хочете видалити це правило?')) {
            await staffCalculationRulesApi.deleteRule(rule.id);
            //toast.success('Правило успішно видалено');
            await loadData();
          }
          break;

        default:
          break;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Помилка виконання дії';
      //toast.error(errorMessage);
    } finally {
      setActiveMenuIndex(null);
    }
  };

  const handleAddRule = () => {
    setSelectedRule(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = async (shouldReload = false) => {
    setIsModalOpen(false);
    setSelectedRule(null);
    
    if (shouldReload) {
      await loadData();
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const ActionsMenu = ({ onActionSelect, onClose, rule, isArchived }) => {
    const actions = isArchived 
      ? [
          { id: 'restore', label: 'Відновити', icon: faEdit },
          { id: 'delete', label: 'Видалити', icon: faTrash }
        ]
      : [
          { id: 'edit', label: 'Редагувати', icon: faEdit },
          { id: 'archive', label: 'Архівувати', icon: faTrash }
        ];

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, rule)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <FontAwesomeIcon icon={action.icon} style={{ width: '16px', color: '#6c757d' }} />
            <span style={{ color: '#212529' }}>{action.label}</span>
          </button>
        ))}
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  if (loading && rules.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Завантаження...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <main className="queuetable-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Панель табов */}
            <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="queuetable-label text-white-50">Правила розрахунку штатних одиниць</span>
                <ul className="queuetable-year-tabs nav nav-tabs">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'active' ? 'active' : ''}`}
                      onClick={() => setActiveTab('active')}
                    >
                      Активні
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${activeTab === 'archived' ? 'active' : ''}`}
                      onClick={() => setActiveTab('archived')}
                    >
                      Архів
                    </button>
                  </li>
                </ul>
              </div>
              <div className="queuetable-switch-wrap d-flex gap-2">
                <button 
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faRefresh} className={loading ? 'fa-spin' : ''} />
                </button>
                <a 
                  href="#" 
                  className="link-primary small text-nowrap" 
                  onClick={(e) => { e.preventDefault(); handleAddRule(); }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Додати правило
                </a>
              </div>
            </div>

            {/* Сообщение об ошибке */}
            {error && (
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                {error}
              </div>
            )}

            {/* Таблица */}
            <div className="card queuetable-table-card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table queuetable-table align-middle mb-0">
                    <thead>
                      <tr>
                        {columns.map((column) => (
                          <th 
                            key={column.key}
                            style={{ width: column.width }}
                            onClick={() => column.sortable && handleSort(column.key)}
                          >
                            <div className="d-flex align-items-center">
                              {column.label}
                              {column.sortable && (
                                <FontAwesomeIcon 
                                  icon={getSortIcon(column.key)} 
                                  className="ms-1" 
                                  style={{ fontSize: '12px' }}
                                />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.length === 0 ? (
                        <tr>
                          <td colSpan={columns.length} className="text-center py-4 text-muted">
                            {activeTab === 'active' 
                              ? 'Немає активних правил' 
                              : 'Немає архівних правил'
                            }
                          </td>
                        </tr>
                      ) : (
                        sortedData.map((rule, index) => (
                          <tr key={rule.id}>
                            <td>
                              <div>
                                <strong>{rule.position.name}</strong>
                                <div className="small text-muted">{rule.position.type}</div>
                              </div>
                            </td>
                            <td>
                              {ruleHelpers.getRuleTypeLabel(rule.ruleType)}
                            </td>
                            <td>{ruleHelpers.getConditionDescription(rule)}</td>
                            <td>{rule.rulePerGroupTxt}</td>
                            <td>
                              <strong>{rule.maxUnits}</strong>
                            </td>
                            <td>{new Date(rule.createdAt).toLocaleDateString('uk-UA')}</td>
                            <td className="queuetable-actions text-end">
                              <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  className="btn btn-sm btn-link text-decoration-none p-0"
                                  onClick={(e) => handleMenuToggle(index, e)}
                                  style={{ color: '#0d6efd' }}
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faEllipsisVertical} />
                                </button>
                                
                                {activeMenuIndex === index && (
                                  <ActionsMenu 
                                    onActionSelect={handleActionSelect}
                                    onClose={handleMenuClose}
                                    rule={rule}
                                    isArchived={activeTab === 'archived'}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Пагинация */}
                <div className="d-flex align-items-center mt-4 queuetable-pagination-wrapper">
                  <div className="queuetable-pagination-info">
                    Всього {sortedData.length} правил
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Модальное окно редактирования правил */}
      <StaffCalculationRuleModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        ruleData={selectedRule}
        workModes={workModes}
        positions={positions}
      />

      {/* Уведомления */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
};

export default StaffCalculationRules;